<script setup lang="ts">
import { computed, ref } from 'vue'
import { ECTABlockButtonAlignment, ECTABlockButtonSizes } from '../CTABlock/ts/types'
import { ETitleBlockAligment } from '../TitleBlock/ts/types'
import { useBrxmData } from '../../composables/useBrxmData'
import { useImage } from '../../composables/useImageUrl'
import { EHeroBannerHeight } from './types'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()
const data = useBrxmData(props, 'HERO_BANNER')
const { isMobile } = useBreakpoint()
const heroBannerRef = ref<HTMLElement | null>(null)

useVisibilityTracking(heroBannerRef, 'HeroBanner', data.value.title || 'HeroBanner')

const { image: responsiveLogo } = useImage(data.value.logoImage, props.page)
const { image: responsiveImage } = useImage(data.value.imageOrVideo, props.page)

const condensed = computed(() =>
    isMobile.value
        ? data.value.mobileHeight === EHeroBannerHeight.CONDENSED
        : data.value.desktopHeight === EHeroBannerHeight.CONDENSED,
)

const getButtonConfig = (buttonData: any) => ({
    ...buttonData,
    size: ECTABlockButtonSizes.MEDIUM,
})

const button1 = computed(() =>
    condensed.value && !data.value.button1 && (data.value.button2 || data.value.link)
        ? undefined
        : getButtonConfig(data.value.button1),
)

const button2 = computed(() =>
    (condensed.value && data.value.button1) || (condensed.value && !data.value.button2 && data.value.link)
        ? undefined
        : getButtonConfig(data.value.button2),
)

const link = computed(() =>
    condensed.value && (data.value.button1 || data.value.button2) ? undefined : getButtonConfig(data.value.link),
)

const isFirst = computed(() => props.component.getId()?.split('_')?.reverse()?.[0] === 'r1')
</script>

<template>
    <BackgroundBlock
        ref="heroBannerRef"
        class="space-background hero-banner relative mx-auto max-w-[2560px] overflow-hidden md:static md:flex md:flex-col"
        :class="[
            condensed ? 'h-[400px] md:h-[440px]' : 'h-[580px] md:h-[586px]',
            { '!mt-[-72px] lg:!mt-[-84px]': isFirst },
        ]"
        :page="page"
        :background="data?.background"
    >
        <div
            :style="data.transparent ? '' : 'background-color: hsl(var(--background))'"
            class="h-[88px] md:flex-none lg:h-[100px]"
        />

        <div class="container md:flex-1">
            <div
                class="max-md:pt-base max-w-full overflow-hidden md:flex"
                :class="[condensed ? 'h-[312px] md:h-[352px] lg:h-[340px]' : 'h-[492px] md:h-[498px] lg:h-[486px]']"
            >
                <div
                    class="relative z-10 md:flex md:flex-col md:justify-center"
                    :class="{
                        ' md:w-[calc(60%-0.5rem)] md:max-w-[calc(60%-0.5rem)] lg:w-[calc(50%-1rem)]': !!responsiveImage,
                        'md:w-1/2': !responsiveImage,
                    }"
                >
                    <div
                        v-if="responsiveLogo && !condensed"
                        class="mb-4 flex"
                    >
                        <BaseImage
                            class="max-h-[40px] max-w-[120px] lg:max-h-[48px] lg:max-w-[144px]"
                            :image-data="responsiveLogo"
                            :alt="data.logoImage?.altText"
                        />
                    </div>

                    <TitleBlockPartsTitleBlockComponent
                        :heading-block-size="data.size"
                        :heading-block-aligment="ETitleBlockAligment.LEFT"
                        :heading="data.title"
                        :seo-heading-size="data?.seoHeadingSize?.value"
                        :subheading="data.subtitle"
                        :paragraph="condensed && data.subtitle ? undefined : data.paragraph?.value"
                        :badges="data.badges"
                        :dark="data?.darkmode"
                    />

                    <CTABlock
                        class="cta-block mt-6"
                        :cta-button1="data.button1 && button1"
                        :cta-button2="data.button2 && button2"
                        :cta-link="data.link && link"
                        :alignment="ECTABlockButtonAlignment.LEFT"
                        :dark="data.darkmode"
                        inline
                        :page="page"
                        component-category="HeroBanner"
                        :component-name="data.title"
                    />
                </div>

                <div
                    v-if="responsiveImage"
                    class="hidden w-4 md:block lg:w-8"
                />

                <div
                    v-if="responsiveImage"
                    class="absolute inset-x-0 bottom-0 z-0 md:static md:flex md:w-[calc(40%-0.5rem)] lg:w-[calc(50%-1rem)]"
                >
                    <BaseImage
                        class="mx-auto"
                        :class="
                            condensed
                                ? 'max-h-[160px] md:max-h-[352px] lg:max-h-[340px]'
                                : 'max-h-[240px] md:max-h-[498px] lg:max-h-[486px]'
                        "
                        :image-data="responsiveImage"
                        :alt="data.imageOrVideo?.altText"
                    />
                </div>
            </div>
        </div>
    </BackgroundBlock>
</template>

<style>
.hero-banner .title-block__paragraph {
  @apply line-clamp-[8];
}
.hero-banner .cta-block button {
  max-width: calc(100vw - 2rem);
}
@media (min-width: theme("screens.md")) {
  .hero-banner .cta-block button {
    max-width: calc(100vw - 3rem);
  }
}
@media (min-width: theme("screens.lg")) {
  .hero-banner .cta-block button {
    max-width: min(1224px, 100vw - 200px);
  }
}
</style>
